import { Button, Center, Flex, Menu } from '@mantine/core';
import { useAuthInfo, useHostedPageUrls, useLogoutFunction, User } from '@propelauth/react';
import { HiArrowRightEndOnRectangle, HiBuildingOffice, HiOutlineUser } from 'react-icons/hi2';
import AvatarWithName from './AvatarWithName';
import { useMemo } from 'react';
import { useUncontrolled } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient';
import { clientIdentity } from '@/context/ApiClientContext/identity';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import useRetrieveProjectsByOrg from '@/hooks/api/projects/useRetrieveProjectsByOrg.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import StyledMenu from '@/components/StyledMenu/StyledMenu.tsx';
import { resetPostHog } from '@/services/posthog.ts';
import classes from './UserMenu.module.css';

type props = {
  user: User;
  includeName?: boolean;
  menuOpened?: boolean;
  setMenuOpened?: (opened: boolean) => void;
};

export function UserMenu({ user, includeName = true, menuOpened, setMenuOpened }: props) {
  const [opened, setOpened] = useUncontrolled({
    value: menuOpened,
    defaultValue: false,
    onChange: setMenuOpened,
  });
  const logoutFunction = useLogoutFunction();
  const { getOrgPageUrl } = useHostedPageUrls();
  const { orgHelper } = useAuthInfo();
  const { orgId, setOrgId, projectId, setProjectId } = useOrgProject();
  const api = useApiClient();
  const forgeAccess = useQuery({
    meta: { suppressGlobalErrorToast: true },
    queryKey: ['forge-access-context', clientIdentity(api)],
    queryFn: async () =>
      (
        await api.get('/internal/forge-access/context', {
          suppressGlobalErrorToast: true,
          handleForbiddenLocally: true,
        })
      ).data,
    enabled: opened,
    retry: false,
    staleTime: 60_000,
  });

  const orgs = useMemo(() => orgHelper?.getOrgs(), [orgHelper]);

  const { data: projects, isPending: loadingProjects } = useRetrieveProjectsByOrg();

  const currentOrgProjects = useMemo(() => {
    if (projects?.results && orgId && orgId in projects.results) return projects.results[orgId];
    return [];
  }, [projects, orgId]);

  const isAdmin = user.email.endsWith('@tryvelos.com');

  return (
    <Center>
      <StyledMenu
        position="right-start"
        styles={{
          dropdown: {
            boxShadow: '0px 6px 18px rgba(0, 0, 0, 0.09)',
          },
        }}
        width={'200px'}
        opened={opened}
        onOpen={() => setOpened(true)}
        onClose={() => setOpened(false)}
      >
        <Menu.Target>
          <Button
            className={undefined}
            variant={'subtle'}
            pl={'sm'}
            pr={'sm'}
            style={{
              marginBottom: '10px',
              paddingTop: '6px',
              paddingBottom: '6px',
              borderRadius: '5px',
              height: '100%',
              // marginLeft: '10px',
              // marginRight: '10px',
              // width: 'calc(100% - 20px)',
              // width: 'max-content',
              // justifyItems: 'flex-start',
              // alignItems: 'center',
              ...(includeName
                ? { width: 'calc(100% - 20px)', justifyItems: 'flex-start' }
                : { width: 'max-content' }),
            }}
            classNames={{ root: classes.button }}
          >
            <AvatarWithName user={user} includeName={includeName} />
          </Button>
        </Menu.Target>

        <Menu.Dropdown onMouseLeave={() => setOpened(false)}>
          <Flex justify={'flex-start'} pl={'xs'}>
            <AvatarWithName user={user} />
          </Flex>
          <Menu.Divider />
          {forgeAccess.data && (
            <Menu.Item
              component="a"
              href={projectId ? `/projects/${projectId}/forge-access` : '/forge/access'}
            >
              Forge access
            </Menu.Item>
          )}
          {isAdmin && orgs && orgs.length > 0 && (
            <>
              <Menu.Label>Organizations</Menu.Label>
              <Menu.Sub>
                <Menu.Sub.Target>
                  <Menu.Sub.Item leftSection={<HiBuildingOffice />}>
                    {orgs.find((o) => o.orgId === orgId)?.orgName || 'No org'}
                  </Menu.Sub.Item>
                </Menu.Sub.Target>

                <Menu.Sub.Dropdown>
                  {orgs.map((org) => (
                    <Menu.Item
                      key={org.orgId}
                      onClick={() => setOrgId(org.orgId, true)}
                      disabled={org.orgId === orgId}
                    >
                      {org.orgName}
                    </Menu.Item>
                  ))}
                </Menu.Sub.Dropdown>
              </Menu.Sub>
              <Menu.Divider />
            </>
          )}

          {isAdmin && currentOrgProjects.length > 0 && (
            <>
              <Menu.Label>Projects</Menu.Label>
              <Menu.Sub>
                <Menu.Sub.Target>
                  <Menu.Sub.Item leftSection={<HiBuildingOffice />}>
                    {currentOrgProjects.find((p) => p.id === projectId)?.name || 'No project'}
                  </Menu.Sub.Item>
                </Menu.Sub.Target>

                <Menu.Sub.Dropdown>
                  {loadingProjects ? (
                    <CustomSpinner />
                  ) : (
                    <>
                      {currentOrgProjects.map((p) => (
                        <Menu.Item
                          key={p.id}
                          onClick={() => setProjectId(p.id, true)}
                          disabled={p.id === projectId}
                        >
                          {p.name}
                        </Menu.Item>
                      ))}
                    </>
                  )}
                </Menu.Sub.Dropdown>
              </Menu.Sub>
              <Menu.Divider />
            </>
          )}

          <Menu.Item
            onClick={() => {
              const url = getOrgPageUrl(orgId);
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
            leftSection={<HiOutlineUser size={18} />}
          >
            Users
          </Menu.Item>

          <Menu.Item
            onClick={async () => {
              await resetPostHog();
              await logoutFunction(true);
            }}
            leftSection={<HiArrowRightEndOnRectangle size={18} />}
          >
            Sign out
          </Menu.Item>
        </Menu.Dropdown>
      </StyledMenu>
    </Center>
  );
}

export default UserMenu;
